// src/PatientMessages.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import './PatientMessages.css';

const DEPT_OPTIONS = [
  { value: 'image_processing', labelKey: 'department.imageProcessing' },
  { value: 'clinical_support', labelKey: 'department.clinicalSupport' },
  { value: 'manufacturing', labelKey: 'department.manufacturing' },
];

function deptLabelFromValue(dept) {
  const found = DEPT_OPTIONS.find((d) => d.value === dept);
  return found?.labelKey || dept || 'department.department';
}

function deptToneFromValue(dept) {
  if (dept === 'manufacturing') return 'warn';
  if (dept === 'clinical_support') return 'ok';
  return 'info';
}

function formatDate(iso, locale) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(locale);
}

function isInternalReply(r) {
  const authorType = String(r?.author_type || '').toLowerCase();
  return (
    authorType.includes('department') ||
    !!r?.department ||
    !!r?.via_manager ||
    String(r?.author_display || '').toLowerCase().includes('manager')
  );
}

function computeLatestInternalReplyTs(messages) {
  let max = 0;
  (messages || []).forEach((m) => {
    (m?.replies || []).forEach((r) => {
      if (!isInternalReply(r)) return;
      // hidden replies should not count as "new"
      if (r?.is_hidden_from_patient) return;
      const t = new Date(r.created_at || 0).getTime();
      if (t > max) max = t;
    });
  });
  return max;
}

const CJK_REGEX =
  /[\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF\u3040-\u309F\u30A0-\u30FF\u3100-\u312F\u31A0-\u31BF\u3000-\u303F\uFF00-\uFFEF]|\u{20000}-\u{2EBEF}/u;

function TranslatableText({ text, enabled }) {
  const { t } = useTranslation();
  const [translated, setTranslated] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [error, setError] = useState(null);

  if (!text) return null;

  const isCJK = CJK_REGEX.test(text);
  const targetLang = isCJK ? 'en' : 'zh-Hans';

  const handleClick = async () => {
    if (!enabled) return;

    if (!showOriginal) {
      setShowOriginal(true);
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const resp = await axios.post('translate/', {
        text,
        to: targetLang,
        from: null,
      });

      const t =
        resp?.data?.translated_text ||
        resp?.data?.translation ||
        resp?.data?.text ||
        (Array.isArray(resp?.data?.translations) ? resp.data.translations?.[0]?.text : '') ||
        '';

      setTranslated(t || '');
      setShowOriginal(false);
    } catch (err) {
      console.error('Translation failed', err);
      setError(t('messages.translateFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!enabled) {
    return <div className="pm-text">{text}</div>;
  }

  const displayText = !showOriginal && translated ? translated : text;
  const buttonLabel = loading
    ? t('translate.translating')
    : showOriginal
    ? isCJK
      ? t('translate.toEnglish')
      : t('translate.toChinese')
    : t('translate.showOriginal');

  return (
    <div className="pm-translatable">
      <div className="pm-text">{displayText}</div>
      <button type="button" className="pm-translate-btn" onClick={handleClick} disabled={loading}>
        {buttonLabel}
      </button>
      {error && <div className="pm-translate-error">{error}</div>}
    </div>
  );
}

export default function PatientMessages({ patientId, active, onUnreadChange }) {
  const { t, i18n } = useTranslation();
  const locale = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US';
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [file, setFile] = useState(null);
  const [department, setDepartment] = useState('image_processing');

  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [hasNewDeptReply, setHasNewDeptReply] = useState(false);
  const [latestActivityTs, setLatestActivityTs] = useState(0);

  const [enableTranslate, setEnableTranslate] = useState(false);

  // UI state
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  // NEW: distributor reply
  const [replyBody, setReplyBody] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);

  const updateNewReplyState = useCallback(
    (msgs) => {
      if (!patientId) return;

      // ✅ only internal (dept/manager) replies count as “new”
      const latest = computeLatestInternalReplyTs(msgs);
      setLatestActivityTs(latest || 0);

      if (!latest) {
        setHasNewDeptReply(false);
        if (onUnreadChange) onUnreadChange(false);
        return;
      }

      const key = `pm_last_seen_dept_reply_${patientId}`;
      let lastSeen = 0;
      try {
        lastSeen = Number(localStorage.getItem(key) || '0');
      } catch {
        lastSeen = 0;
      }

      const hasNew = !lastSeen || latest > lastSeen;
      setHasNewDeptReply(hasNew);
      if (onUnreadChange) onUnreadChange(hasNew);
    },
    [patientId, onUnreadChange]
  );

  const fetchMessages = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get('patient-messages/', {
        params: { patient: patientId },
      });

      // server already filters hidden threads + hidden replies
      const data = resp.data || [];
      const safe = Array.isArray(data) ? data : [];
      setMessages(safe);
      updateNewReplyState(safe);

      if (safe.length > 0) {
        const newestId = safe[0]?.id;
        setSelectedId((prev) => prev || newestId);
      } else {
        setSelectedId(null);
      }
    } catch (err) {
      console.error('Failed to load messages', err);
      setError(t('messages.noPermission'));
    } finally {
      setLoading(false);
    }
  }, [patientId, updateNewReplyState]);

  useEffect(() => {
    if (active) fetchMessages();
  }, [active, fetchMessages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patientId) {
      setError(t('messages.noPatient'));
      return;
    }
    if (!subject.trim() || !body.trim()) {
      setError(t('messages.subjectBodyRequired'));
      return;
    }

    setSending(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const formData = new FormData();
      formData.append('patient', patientId);
      formData.append('subject', subject.trim());
      formData.append('body', body.trim());
      formData.append('department', department);
      if (file) formData.append('attachment', file);

      await axios.post('patient-messages/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSubject('');
      setBody('');
      setFile(null);
      setSuccessMsg(t('messages.sentToDepartment'));
      await fetchMessages();
    } catch (err) {
      console.error('Failed to send message', err);
      setError(t('messages.sendFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!selectedId) return;

    setError(null);
    setSuccessMsg(null);

    if (!replyBody.trim() && !replyFile) {
      setError(t('messages.replyRequired'));
      return;
    }

    setSendingReply(true);
    try {
      const formData = new FormData();
      formData.append('body', replyBody.trim());
      if (replyFile) formData.append('attachment', replyFile);

      await axios.post(`patient-messages/${selectedId}/reply/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setReplyBody('');
      setReplyFile(null);
      setSuccessMsg(t('messages.replySent'));
      await fetchMessages();
    } catch (err) {
      console.error('Failed to send reply', err);
      const isChinese = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh');
      setError(!isChinese && err?.response?.data?.detail ? err.response.data.detail : t('messages.failedSendReply'));
    } finally {
      setSendingReply(false);
    }
  };

  const handleMarkRepliesRead = () => {
    if (!patientId || !latestActivityTs) {
      setHasNewDeptReply(false);
      if (onUnreadChange) onUnreadChange(false);
      return;
    }
    const key = `pm_last_seen_dept_reply_${patientId}`;
    try {
      localStorage.setItem(key, String(latestActivityTs));
    } catch {
      // ignore
    }
    setHasNewDeptReply(false);
    if (onUnreadChange) onUnreadChange(false);
  };

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return messages || [];
    return (messages || []).filter((m) => {
      const hay = [m.subject, m.body, m.department_display, m.location_display, m.sender_display]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [messages, search]);

  const selected = useMemo(() => {
    if (!selectedId) return null;
    return (messages || []).find((m) => m.id === selectedId) || null;
  }, [messages, selectedId]);

  // ✅ extra safety: never show hidden replies in this tab
  const visibleReplies = useMemo(() => {
    const replies = selected?.replies || [];
    return replies.filter((r) => !r?.is_hidden_from_patient);
  }, [selected]);

  return (
    <div className="patient-messages">
      <div className="pm-top">
        <div className="pm-title">
          <div className="pm-title-left">
            <h3>
              {t('messages.patientMessages')}
              {hasNewDeptReply && <span className="pm-badge">!</span>}
            </h3>
            <div className="pm-subtitle">
              {t('messages.subtitle')}
            </div>
          </div>

          <div className="pm-title-actions">
            <button type="button" className="pm-btn" onClick={fetchMessages} disabled={loading}>
              {loading ? t('messages.refreshing') : t('refresh')}
            </button>

            {hasNewDeptReply && (
              <button type="button" className="pm-btn danger" onClick={handleMarkRepliesRead}>
                {t('messages.markRepliesRead')}
              </button>
            )}

            <label className="pm-toggle">
              <input
                type="checkbox"
                checked={enableTranslate}
                onChange={(e) => setEnableTranslate(e.target.checked)}
              />
              <span>{t('messages.translationTools')}</span>
            </label>
          </div>
        </div>

        {error && <div className="pm-alert pm-alert-error">{error}</div>}
        {successMsg && <div className="pm-alert pm-alert-success">{successMsg}</div>}
      </div>

      <div className="pm-grid">
        {/* LEFT: compose + list */}
        <div className="pm-left">
          <div className="pm-card">
            <div className="pm-card-title">{t('messages.sendNew')}</div>

            <form className="patient-messages-form" onSubmit={handleSubmit}>
              <div className="pm-field">
                <label>{t('department.department')}</label>
                <select value={department} onChange={(e) => setDepartment(e.target.value)}>
                  {DEPT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pm-field">
                <label>{t('subject')}</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={255}
                />
              </div>

              <div className="pm-field">
                <label>{t('message')}</label>
                <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
              </div>

              <div className="pm-field">
                <label>{t('attachmentOptional')}</label>
                <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
              </div>

              <div className="pm-actions">
                <button type="submit" className="pm-btn primary" disabled={sending}>
                  {sending ? t('messages.sending') : t('messages.sendMessage')}
                </button>
              </div>
            </form>
          </div>

          <div className="pm-card">
            <div className="pm-card-title">{t('messages.threads')}</div>

            <input
              className="pm-search"
              placeholder={t('messages.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {loading ? (
              <div className="pm-muted">{t('messages.loading')}</div>
            ) : filtered.length === 0 ? (
              <div className="pm-muted">{t('messages.none')}</div>
            ) : (
              <div className="pm-thread-list">
                {filtered.map((m) => {
                  const activeRow = m.id === selectedId;
                  const dept = m.department || '';
                  const deptLabel = m.department_display || t(deptLabelFromValue(dept));
                  const tone = deptToneFromValue(dept);

                  return (
                    <button
                      key={m.id}
                      type="button"
                      className={`pm-thread ${activeRow ? 'active' : ''}`}
                      onClick={() => setSelectedId(m.id)}
                    >
                      <div className="pm-thread-top">
                        <div className="pm-thread-subject">{m.subject || t('messages.noSubject')}</div>
                        <div className="pm-thread-date">{formatDate(m.created_at, locale)}</div>
                      </div>
                      <div className="pm-thread-meta">
                        <span className={`pm-pill ${tone}`}>{deptLabel}</span>
                        {m.location_display ? <span className="pm-muted"> · {m.location_display}</span> : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: conversation */}
        <div className="pm-right">
          <div className="pm-card pm-convo">
            {!selected ? (
              <div className="pm-empty">
                <div className="pm-empty-title">{t('messages.selectThread')}</div>
                <div className="pm-muted">{t('messages.selectThreadHint')}</div>
              </div>
            ) : (
              <>
                <div className="pm-convo-header">
                  <div className="pm-convo-title">
                    <div className="pm-convo-subject">{selected.subject || t('messages.noSubject')}</div>
                    <div className="pm-muted pm-convo-subline">
                      {t('messages.thread')} #{selected.id} · {selected.location_display ? `${selected.location_display} · ` : ''}
                      {selected.department_display || t(deptLabelFromValue(selected.department))}
                    </div>
                  </div>
                  <div className="pm-convo-meta pm-muted">{formatDate(selected.created_at, locale)}</div>
                </div>

                <div className="pm-convo-body">
                  {/* Root */}
                  {(() => {
                    const rootIsInternal = !!selected.via_manager;
                    const side = rootIsInternal ? 'left' : 'right';

                    const who = rootIsInternal ? (selected.sender_display || t('department.department')) : t('messages.you');
                    const role = rootIsInternal ? t('department.department') : t('messages.distributor');

                    return (
                      <div className={`pm-bubble-row ${side}`}>
                        <div className={`pm-bubble ${side}`}>
                          <div className="pm-bubble-top">
                            <div className="pm-bubble-who">
                              <span className="pm-who">{who}</span>
                              <span className="pm-role">{role}</span>
                              <span className={`pm-pill ${deptToneFromValue(selected.department)}`}>
                                {t('messages.to')} {selected.department_display || t(deptLabelFromValue(selected.department))}
                              </span>
                            </div>
                            <div className="pm-bubble-when">{formatDate(selected.created_at, locale)}</div>
                          </div>

                          <TranslatableText text={selected.body || ''} enabled={enableTranslate} />

                          {selected.attachment && (
                            <div className="pm-link">
                              <a href={selected.attachment} target="_blank" rel="noreferrer">
                                {t('messages.viewAttachment')}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Replies */}
                  {visibleReplies.map((r) => {
                    const isDept = isInternalReply(r);
                    const side = isDept ? 'left' : 'right';

                    const deptValue = r.department || selected.department;
                    const deptLabel = r.department_display || t(deptLabelFromValue(deptValue));
                    const tone = deptToneFromValue(deptValue);

                    const who = isDept ? (r.author_display || deptLabel) : (r.author_display || t('messages.you'));
                    const role = isDept ? t('department.department') : t('messages.distributor');

                    return (
                      <div key={r.id} className={`pm-bubble-row ${side}`}>
                        <div className={`pm-bubble ${side}`}>
                          <div className="pm-bubble-top">
                            <div className="pm-bubble-who">
                              <span className="pm-who">{who}</span>
                              <span className="pm-role">{role}</span>
                              {isDept ? <span className={`pm-pill ${tone}`}>{deptLabel}</span> : null}
                              <span className="pm-thread-ref">{t('messages.inThread')} #{selected.id}</span>
                            </div>
                            <div className="pm-bubble-when">{formatDate(r.created_at, locale)}</div>
                          </div>

                          <TranslatableText text={r.body || ''} enabled={enableTranslate} />

                          {r.attachment && (
                            <div className="pm-link">
                              <a href={r.attachment} target="_blank" rel="noreferrer">
                                {t('messages.viewAttachment')}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* ✅ Distributor reply composer */}
                <div className="pm-convo-compose">
                  <form className="pm-compose" onSubmit={handleSendReply}>
                    <textarea
                      rows={3}
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      placeholder={t('messages.replyPlaceholder')}
                    />
                    <div className="pm-compose-actions">
                      <div className="pm-file">
                        <label className="pm-muted">{t('attachment')}</label>
                        <input type="file" onChange={(e) => setReplyFile(e.target.files?.[0] || null)} />
                      </div>
                      <button className="pm-btn primary" type="submit" disabled={sendingReply}>
                        {sendingReply ? t('messages.sending') : t('messages.sendReply')}
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}